import { env } from '@/env';
import type {
  EligibilitySubmitResponse,
  EligibilityResultResponse,
  AlignmentSubmitResponse,
  AlignmentResultResponse,
} from '@/services/cmsApiClient';

/**
 * Internal-testing pilot mock (RND-33872).
 *
 * Friends-and-family testers run the whole webreg happy path on staging with
 * zero CMS dependency: every CMS Integration Service call (eligibility,
 * alignment) short-circuits to a canned success response instead of hitting
 * the network, as does one side-effect-only hello-server call (download SMS).
 *
 * Scope limit (RND-37449): promo code, shipping details and signup are NOT
 * mocked, and no mock factory for them exists here — a canned response left in
 * the file is one `if (isPilotMock())` away from reinstating the regression that
 * forced the PR #96 revert (staging registration looked complete while creating
 * no account and no shipping record). Phone OTP (start/checkCode) is likewise
 * never mocked.
 *
 * Known exception to "CMS mocks only": `downloadSMS` is still short-circuited
 * under `isPilotMock()`, so on the F&F staging host the Download App step
 * reports success while no SMS is sent. Pre-existing and side-effect-only, but
 * it is the same "UI reports success, nothing happened" shape as the #96
 * regression. `recordFailedRegistration` had the same shape and was narrowed to
 * the dev server alone in RND-38229 (`import.meta.env.DEV`), because a promo cap
 * that goes unrecorded on staging is the whole failure this ticket exists to
 * make visible, and the e2e harness has to see the real request.
 *
 * Allowlist of Vite `--mode` values (fail closed for unknown future modes).
 * Production is never listed. Staging stays allowed for F&F API mocking;
 * credential persistence and the `window.__pilotE2e` console hook are gated
 * separately (DEV / e2e only) so deployed staging builds do not expose that
 * backdoor. NOTE: the signup state/zip placeholder is NOT among them — it is
 * unconditional on every environment (`OtpAccountStep.tsx`), production
 * included; the old isPilotE2eRuntime() gate on it is gone.
 */
const PILOT_MOCK_ALLOWED_MODES = new Set(['development', 'staging', 'e2e', 'automation']);

export const isPilotMock = (): boolean =>
  env.PILOT_MOCK_ENABLED === 'true' && PILOT_MOCK_ALLOWED_MODES.has(env.MODE);

/**
 * Narrower than isPilotMock(): true only on the Vite dev server or an explicit
 * `e2e` build. Deployable staging/production builds have DEV=false and
 * MODE≠e2e, so credential persistence and the console seed hook stay off the
 * patient-reachable F&F host while API mocks can still run.
 *
 * It does NOT cover the signup state/zip placeholder — that is unconditional on
 * every environment. Claiming otherwise here previously made the AK/99999 issue
 * look e2e-only when it reaches real patients (PR #101 review).
 *
 * Uses literal `import.meta.env` so Vite can tree-shake call sites.
 */
export const isPilotE2eRuntime = (): boolean =>
  import.meta.env.DEV || import.meta.env.MODE === 'e2e';

/**
 * A hand-driven local dev server, and nothing else: `e2e` is excluded even
 * though Playwright runs `vite --mode e2e`, which is DEV. The
 * failed-registration report uses this (RND-38229) — the e2e harness intercepts
 * that request and has to see it, while a local dev run has no hello-server to
 * talk to. Literal `import.meta.env` for the same tree-shaking reason as above;
 * a named export so tests can drive it.
 */
export const isDevServer = (): boolean =>
  import.meta.env.DEV && import.meta.env.MODE !== 'e2e';

let mockSubmissionCounter = 0;
const nextMockSubmissionId = (kind: 'eligibility' | 'alignment'): string => {
  mockSubmissionCounter += 1;
  return `pilot-mock-${kind}-${Date.now()}-${mockSubmissionCounter}`;
};

export const mockPostEligibility = (): EligibilitySubmitResponse => ({
  submissionId: nextMockSubmissionId('eligibility'),
});

// Always resolves eligible, regardless of track/submissionId — the pilot
// forces the eCKM track eligible so testers always reach
// the end of the questionnaire (see useEligibilityFinalize's pilot-mock path).
export const mockGetEligibilityResult = (): EligibilityResultResponse => ({
  status: 'complete',
  resultCode: 'eligible',
});

export const mockPostAlignment = (): AlignmentSubmitResponse => ({
  submissionId: nextMockSubmissionId('alignment'),
});

export const mockGetAlignmentResult = (submissionId: string): AlignmentResultResponse => ({
  submissionId,
  status: 'complete',
  resultCode: 'aligned',
  outcome: 'enrolled',
});

export const mockPostClaim = async (): Promise<void> => undefined;

export const MOCK_DOWNLOAD_SMS_RESPONSE = 'Success';

export const mockRecordFailedRegistration = (): { status: string } => ({ status: 'ok' });

// Deliberately no mockSignup / mockPromoCode / mockShippingDetails: account
// creation, promo lookup and shipping must always hit hello-server so F&F testing
// on staging produces real accounts and real shipping records (RND-37449).
